import { useState, useEffect, useCallback } from "react";
import { Download, Smartphone, X } from "lucide-react";

const IS_MOBILE =
  typeof navigator !== "undefined" &&
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

export default function InstallAppButton({ variant = "footer" }) {
  const [promptReady, setPromptReady] = useState(!!window.deferredPrompt);
  const [installed, setInstalled] = useState(!!window.__pwaInstalled);
  const [showDesktopMsg, setShowDesktopMsg] = useState(false);

  useEffect(() => {
    const onReady = () => setPromptReady(true);
    const onInstalled = () => {
      setInstalled(true);
      setPromptReady(false);
    };

    window.addEventListener("pwa-prompt-ready", onReady);
    window.addEventListener("pwa-installed", onInstalled);

    // Check standalone on mount
    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone
    ) {
      setInstalled(true);
    }

    return () => {
      window.removeEventListener("pwa-prompt-ready", onReady);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const handleClick = useCallback(async () => {
    // Already installed
    if (installed) return;

    // Desktop — show friendly message
    if (!IS_MOBILE) {
      setShowDesktopMsg(true);
      return;
    }

    // Mobile — try to trigger the stored prompt
    const prompt = window.deferredPrompt;
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      // The event is consumed — clear it. Browser may fire a new one later.
      window.deferredPrompt = null;
      setPromptReady(false);

      if (outcome === "accepted") {
        setInstalled(true);
      }
      // If dismissed, the button stays visible. The browser *may* fire
      // beforeinstallprompt again later (varies by browser).
    } else {
      // On mobile but no stored prompt — could be Safari/Firefox or
      // the browser hasn't fired the event yet.
      setShowDesktopMsg(true);
    }
  }, [installed]);

  // Don't render if already installed
  if (installed) return null;

  // ─── Mobile-menu variant ───
  if (variant === "mobile-menu") {
    return (
      <>
        <button
          data-testid="install-app-mobile-menu"
          onClick={handleClick}
          className="flex items-center gap-2 px-3 py-3 border-l-4 border-[#14B8A6] text-[#14B8A6] hover:bg-[#14B8A6]/5 transition-colors font-pixel text-sm w-full text-left"
        >
          <Download size={16} />
          {promptReady ? "Install App" : "Get the App"}
        </button>

        {showDesktopMsg && (
          <DesktopModal onClose={() => setShowDesktopMsg(false)} isMobile={IS_MOBILE} />
        )}
      </>
    );
  }

  // ─── Footer variant (default) ───
  return (
    <>
      <button
        data-testid="install-app-footer"
        onClick={handleClick}
        className="group inline-flex items-center gap-2 text-sm text-[#14B8A6] hover:text-[#2DD4BF] transition-colors"
      >
        <Download
          size={16}
          className="transition-transform group-hover:-translate-y-0.5"
        />
        <span>Install App</span>
      </button>

      {showDesktopMsg && (
        <DesktopModal onClose={() => setShowDesktopMsg(false)} isMobile={IS_MOBILE} />
      )}
    </>
  );
}

/* Overlay shown on desktop or when no prompt is available */
function DesktopModal({ onClose, isMobile }) {
  return (
    <div
      data-testid="install-app-desktop-modal"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(15, 23, 42, 0.7)",
        backdropFilter: "blur(4px)",
        padding: "1rem",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#1E293B",
          border: "1px solid rgba(20, 184, 166, 0.3)",
          borderRadius: "16px",
          padding: "2rem",
          maxWidth: "380px",
          width: "100%",
          textAlign: "center",
          position: "relative",
          animation: "installModalIn 0.25s ease-out",
        }}
      >
        <button
          data-testid="install-modal-close"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            background: "none",
            border: "none",
            color: "#64748B",
            cursor: "pointer",
            padding: "4px",
          }}
        >
          <X size={18} />
        </button>

        <div
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "16px",
            background: "linear-gradient(135deg, #14B8A6, #0D9488)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 1.25rem",
          }}
        >
          <Smartphone size={30} color="#F8FAFC" />
        </div>

        <h3
          style={{
            color: "#F8FAFC",
            fontSize: "1.1rem",
            fontWeight: 700,
            margin: "0 0 0.5rem",
          }}
        >
          {isMobile ? "Almost There!" : "Install on Your Phone"}
        </h3>

        <p
          style={{
            color: "#94A3B8",
            fontSize: "0.875rem",
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          {isMobile
            ? "The install option will be available after a few more visits. Keep browsing and try again soon!"
            : "For the best experience, open this site on your mobile device to install the Vallarta Pixel app directly to your home screen."}
        </p>

        <p
          style={{
            color: "#64748B",
            fontSize: "0.75rem",
            marginTop: "1rem",
            lineHeight: 1.5,
          }}
        >
          {isMobile
            ? "Tip: Use Chrome or Brave on Android for the best PWA support."
            : "Visit vallartapixel.com on Android Chrome or Brave."}
        </p>

        <button
          data-testid="install-modal-ok"
          onClick={onClose}
          style={{
            marginTop: "1.25rem",
            padding: "0.625rem 2rem",
            background: "#14B8A6",
            color: "#0F172A",
            border: "none",
            borderRadius: "50px",
            fontSize: "0.875rem",
            fontWeight: 700,
            cursor: "pointer",
            transition: "background 0.2s",
          }}
          onMouseOver={(e) => (e.target.style.background = "#0D9488")}
          onMouseOut={(e) => (e.target.style.background = "#14B8A6")}
        >
          Got It
        </button>
      </div>

      <style>{`
        @keyframes installModalIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
